import { Field, ErrorMessage } from "formik"
import Select from "react-select"
import * as Yup from "yup"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { currencyOptions } from "../../utils/currencyOptions";
import { formatNumberWithCommas } from "../../utils/formatNumber";


const bankOptions = [
  { value: "sbi", label: "State Bank of India" },
  { value: "hdfc", label: "HDFC Bank" },
  { value: "icici", label: "ICICI Bank" },
  { value: "axis", label: "Axis Bank" },
  { value: "kotak", label: "Kotak Mahindra Bank" },
]

const frequencyOptions = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "annually", label: "Annually" },
]

export const overdraftInitialValues = {
  name: "",
  bankName: "",
  overdraftLimit: "",
  overdraftStartDate: null,
  currentOverdraftAmount: "",
  overdraftFee: "",
  repaymentFrequency: "",
  interestRate: "",
  currency: "",
}

export const overdraftValidationSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, "Min length: 3 characters")
    .max(100, "Max length: 100 characters")
    .matches(
      /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
      "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
    )
    .required("Name is required"),
  bankName: Yup.string().required("Bank/Account name is required"),
  overdraftLimit: Yup.number().typeError("Must be a number").required("Overdraft limit is required").min(0, "Must be >= 0"),
  overdraftStartDate: Yup.date().required("Overdraft start date is required"),
  currentOverdraftAmount: Yup.number().typeError("Must be a number").required("Current overdraft amount is required").min(0, "Must be >= 0"),
  overdraftFee: Yup.number().typeError("Must be a number").required("Overdraft fee is required").min(0, "Must be >= 0"),
  repaymentFrequency: Yup.string().required("Repayment frequency is required"),
  interestRate: Yup.string().required("Interest rate is required"),
  currency: Yup.string().required("Currency is required"),
})

const OverdraftForm = ({ values, setFieldValue, errors, touched, localReadOnly }) => {
  return (
    <>
      <div className="col-md-12 mb-4">
            <label className="form-label">
              Liability Name<span className="text-danger">*</span>
            </label>
            <Field
              type="text"
              name="name"
              placeholder="e.g., Purchase on Klarna, Afterpay"
              readOnly={localReadOnly}
             isDisabled={localReadOnly}
              className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
            />
            <ErrorMessage name="name" component="div" className="invalid-feedback" />
          </div>
    <div className="row">
      {/* Bank/Account Name */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Bank/Account Name<span className="text-danger">*</span></label>
        <Select
          options={bankOptions}
          value={bankOptions.find((option) => option.value === values.bankName)}
          onChange={(option) => setFieldValue("bankName", option.value)}
          className="react-select-container"
          classNamePrefix="react-select"
        readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="bankName" component="div" className="text-danger small" />
      </div>

      {/* Overdraft Limit + Currency */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Overdraft Limit<span className="text-danger">*</span></label>
      
          <Field
            type="text"
            inputMode="decimal"
            name="overdraftLimit"
            placeholder="e.g., 1000"
            className={`form-control ${errors.overdraftLimit && touched.overdraftLimit ? "is-invalid" : ""}`}
            style={{ flex: 2 }}
            readOnly={localReadOnly}
             isDisabled={localReadOnly}
            value={values.overdraftLimit ? formatNumberWithCommas(values.overdraftLimit) : ""}
            onChange={(e) => {
              let unformatted = e.target.value.replace(/[^\d.]/g, "")
              const parts = unformatted.split(".")
              if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
              if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
              setFieldValue("overdraftLimit", unformatted || "")
            }}
          />
       
        <ErrorMessage name="overdraftLimit" component="div" className="invalid-feedback" />
        
      </div>

      {/* Overdraft Start Date with react-datepicker */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Overdraft Start Date<span className="text-danger">*</span></label>
        <DatePicker
          selected={values.overdraftStartDate ? new Date(values.overdraftStartDate) : null}
          onChange={(date) => setFieldValue("overdraftStartDate", date)}
          dateFormat="yyyy-MM-dd"
            placeholderText="Select date"
              showMonthDropdown
                                          showYearDropdown
                                          dropdownMode="select"
                                          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.overdraftStartDate && touched.overdraftStartDate ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="overdraftStartDate" component="div" className="text-danger small" />
      </div>

      {/* Current Overdraft Amount */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Current Overdraft Amount<span className="text-danger">*</span></label>
          <div className="d-flex gap-2">
                  <Select
            options={currencyOptions}
            value={currencyOptions.find(option => option.value === values.currency)}
            onChange={option => setFieldValue("currency", option.value)}
            className="currency-select"
            classNamePrefix="react-select"
            styles={{ container: base => ({ ...base, minWidth: 120 }) }}
          />
        <Field
          type="text"
          inputMode="decimal"
          name="currentOverdraftAmount"
          placeholder="e.g., 200"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.currentOverdraftAmount && touched.currentOverdraftAmount ? "is-invalid" : ""}`}
          value={values.currentOverdraftAmount ? formatNumberWithCommas(values.currentOverdraftAmount) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("currentOverdraftAmount", unformatted || "")
          }}
        />
        </div>
        <ErrorMessage name="currentOverdraftAmount" component="div" className="invalid-feedback" />
        <ErrorMessage name="currency" component="div" className="invalid-feedback" />
      </div>

      {/* Overdraft Fee */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Overdraft Fee<span className="text-danger">*</span></label>
        <Field
          type="text"
          inputMode="decimal"
          name="overdraftFee"
          placeholder="e.g., 15"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.overdraftFee && touched.overdraftFee ? "is-invalid" : ""}`}
          value={values.overdraftFee ? formatNumberWithCommas(values.overdraftFee) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("overdraftFee", unformatted || "")
          }}
        />
        <ErrorMessage name="overdraftFee" component="div" className="invalid-feedback" />
      </div>

      {/* Repayment Frequency */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Repayment Frequency<span className="text-danger">*</span></label>
        <Select
          options={frequencyOptions}
          value={frequencyOptions.find((option) => option.value === values.repaymentFrequency)}
          onChange={(option) => setFieldValue("repaymentFrequency", option.value)}
          className="react-select-container"
          classNamePrefix="react-select"
         readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="repaymentFrequency" component="div" className="text-danger small" />
      </div>

      {/* Interest Rate */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Interest Rate<span className="text-danger">*</span></label>
        <Field
          type="text"
          name="interestRate"
          placeholder="e.g., 12.5%"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.interestRate && touched.interestRate ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="interestRate" component="div" className="invalid-feedback" />
      </div>
    </div>
    </>
  )
}

export default OverdraftForm
